/**
 * Case chat WebSocket: one connection per caseId while active.
 * Auth: httpOnly session cookies on the upgrade request (same origin as REST).
 */

import environmentService from "../config/environment.config";
import type { ChatResponse } from "../types/chat";
import type {
  ChatWsInboundServerMessage,
  ChatWsMessageCompleteMessage,
  ChatWsUserMessage,
} from "../types/chatWs";
import { parseChatWsInboundMessage } from "../types/chatWs";

const SEND_TIMEOUT_MS = 120_000;

type Pending = {
  resolve: (value: ChatResponse) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

function buildCaseChatWsUrl(caseId: number): string {
  const path = `/api/ws/chat/cases/${caseId}`;
  return environmentService.buildWebSocketUrl(path);
}

function toChatResponse(complete: ChatWsMessageCompleteMessage): ChatResponse {
  return {
    message: complete.message ?? "",
    detectedIntent: complete.detectedIntent ?? "",
    intentConfidence: complete.intentConfidence ?? 0,
    suggestedFollowUps: complete.suggestedFollowUps ?? [],
    sessionId: complete.sessionId ?? "",
    conversationId: complete.conversationId ?? "",
  };
}

class CaseChatSocket {
  readonly caseId: number;
  private ws: WebSocket | null = null;
  private backendSessionId: string | null = null;
  private readyPromise: Promise<string> | null = null;
  private pending = new Map<string, Pending>();
  private connectGen = 0;

  constructor(caseId: number) {
    this.caseId = caseId;
  }

  get sessionId(): string | null {
    return this.backendSessionId;
  }

  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Connect and wait for SESSION_READY; returns chat session UUID.
   */
  ensureReady(): Promise<string> {
    if (this.backendSessionId && this.isOpen()) {
      return Promise.resolve(this.backendSessionId);
    }

    if (this.readyPromise) {
      return this.readyPromise;
    }

    this.readyPromise = new Promise<string>((resolve, reject) => {
      const gen = ++this.connectGen;

      try {
        if (this.ws) {
          try {
            this.ws.close();
          } catch {
            /* ignore */
          }
          this.ws = null;
        }

        const url = buildCaseChatWsUrl(this.caseId);
        const socket = new WebSocket(url);
        this.ws = socket;

        const fail = (err: Error) => {
          if (gen !== this.connectGen) return;
          this.cleanupSocket();
          reject(err);
        };

        socket.onerror = () => {
          fail(new Error("WebSocket connection failed"));
        };

        socket.onclose = () => {
          this.rejectAllPending(new Error("WebSocket closed"));
          if (gen === this.connectGen) {
            this.ws = null;
            this.backendSessionId = null;
            this.readyPromise = null;
          }
        };

        socket.onmessage = (event: MessageEvent<string>) => {
          if (gen !== this.connectGen) return;
          let data: unknown;
          try {
            data = JSON.parse(event.data);
          } catch {
            return;
          }
          const parsed = parseChatWsInboundMessage(data);
          if (!parsed) return;
          this.handleServerMessage(parsed, resolve, fail, gen);
        };
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
        this.readyPromise = null;
      }
    }).finally(() => {
      this.readyPromise = null;
    });

    return this.readyPromise;
  }

  private handleServerMessage(
    data: ChatWsInboundServerMessage,
    resolveReady: (id: string) => void,
    fail: (e: Error) => void,
    gen: number,
  ): void {
    if (data.type === "SESSION_READY") {
      this.backendSessionId = data.sessionId;
      resolveReady(data.sessionId);
      return;
    }

    if (data.type === "MESSAGE_COMPLETE") {
      const cid = data.correlationId;
      if (cid === null || cid === undefined || cid === "") {
        return;
      }
      const entry = this.pending.get(cid);
      if (!entry) return;
      clearTimeout(entry.timer);
      this.pending.delete(cid);
      entry.resolve(toChatResponse(data));
      return;
    }

    if (data.type === "ERROR") {
      const cid = data.correlationId;
      const err = new Error(data.message || data.code || "Chat error");
      if (cid) {
        const entry = this.pending.get(cid);
        if (entry) {
          clearTimeout(entry.timer);
          this.pending.delete(cid);
          entry.reject(err);
          return;
        }
      }
      if (gen === this.connectGen && !this.backendSessionId) {
        fail(err);
      }
    }
  }

  sendMessage(text: string): Promise<ChatResponse> {
    if (!this.isOpen()) {
      return Promise.reject(new Error("WebSocket is not connected"));
    }

    const correlationId = crypto.randomUUID();
    const payload: ChatWsUserMessage = {
      type: "USER_MESSAGE",
      correlationId,
      message: text,
    };

    return new Promise<ChatResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(correlationId);
        reject(new Error("Chat response timed out"));
        if (this.pending.size === 0) {
          this.closeStaleSocketOnly();
        }
      }, SEND_TIMEOUT_MS);

      this.pending.set(correlationId, { resolve, reject, timer });

      try {
        this.ws!.send(JSON.stringify(payload));
      } catch (e) {
        clearTimeout(timer);
        this.pending.delete(correlationId);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  private rejectAllPending(err: Error): void {
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.pending.clear();
  }

  /** Close WS after a stray timeout so the browser/OS release resources; avoids hung OPEN state when no callers remain. */
  private closeStaleSocketOnly(): void {
    const ws = this.ws;
    if (!ws) return;
    try {
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(1000, "reply_timeout");
      }
    } catch {
      /* ignore */
    }
    this.ws = null;
    this.backendSessionId = null;
    this.connectGen++;
  }

  private cleanupSocket(): void {
    this.connectGen++;
    if (this.ws) {
      try {
        this.ws.onclose = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.backendSessionId = null;
    this.rejectAllPending(new Error("WebSocket disconnected"));
  }

  close(): void {
    this.cleanupSocket();
    this.readyPromise = null;
  }
}

class CaseChatWebSocketManager {
  private readonly byCase = new Map<number, CaseChatSocket>();

  /**
   * Ensure socket is up and SESSION_READY received; returns backend session UUID.
   */
  async ensureConnected(caseId: number): Promise<string> {
    let socket = this.byCase.get(caseId);
    if (!socket) {
      socket = new CaseChatSocket(caseId);
      this.byCase.set(caseId, socket);
    }
    return socket.ensureReady();
  }

  async send(caseId: number, message: string): Promise<ChatResponse> {
    const socket = this.byCase.get(caseId);
    if (!socket || !socket.isOpen()) {
      await this.ensureConnected(caseId);
    }
    const active = this.byCase.get(caseId)!;
    return active.sendMessage(message);
  }

  isCaseConnected(caseId: number): boolean {
    return this.byCase.get(caseId)?.isOpen() ?? false;
  }

  closeCase(caseId: number): void {
    const s = this.byCase.get(caseId);
    if (s) {
      s.close();
      this.byCase.delete(caseId);
    }
  }

  closeAll(): void {
    for (const s of this.byCase.values()) {
      s.close();
    }
    this.byCase.clear();
  }
}

export const caseChatWebSocketManager = new CaseChatWebSocketManager();
